import { COOKIE_KEY, isShareableLandingPath, Paths, SEARCH_PARAMS, STORAGE_KEY } from "@constants";
import { deleteCookie, getCookie, setCookie } from "@utils/cookie";
import { getRuntimeContext } from "@utils/pwa";
import { safeSessionGetItemWithSource, safeSessionRemoveItem, safeSessionSetItem } from "@utils/storage";
import { generatePath } from "react-router-dom";

export function buildMemberReferralAbsoluteUrl(
  refCode: string | undefined | null,
  destination?: string | null,
): string {
  const siteUrl = `${window.location.protocol}//${window.location.host}`;
  const code = refCode?.trim() || "????";
  const url = siteUrl + generatePath(Paths.PUBLIC.REFERRAL_CALLBACK, { refCode: code });
  if (isShareableLandingPath(destination)) {
    const params = new URLSearchParams();
    params.set(SEARCH_PARAMS.NEXT, destination);
    return `${url}?${params.toString()}`;
  }
  return url;
}

export type ReferralCodeSource = "session" | "memory" | "url" | "cookie" | "none";

/** Lets the installed app pick up a code from a link opened in the browser. */
const REF_CODE_COOKIE_MAX_AGE = 7 * 24 * 60 * 60; // 7 days, in seconds
const REF_CODE_FORMAT = /^[A-Za-z0-9_-]{1,32}$/;

/** Latched on use or reject, so the URL fallback cannot resurrect the code. */
let consumed = false;

const stripReferralCodeFromUrl = () => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(SEARCH_PARAMS.REF_CODE)) return;
  url.searchParams.delete(SEARCH_PARAMS.REF_CODE);
  // Keep history.state: react-router stores key/idx there
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
};

export const storeReferralCode = (refCode: string | undefined | null) => {
  if (refCode && !consumed) {
    // A tab that already holds this code must not put the cookie back after the code was used in another window
    const isNewInThisTab = safeSessionGetItemWithSource(STORAGE_KEY.REF_CODE).value !== refCode;
    safeSessionSetItem(STORAGE_KEY.REF_CODE, refCode);
    if (isNewInThisTab && REF_CODE_FORMAT.test(refCode))
      setCookie(COOKIE_KEY.REF_CODE, refCode, REF_CODE_COOKIE_MAX_AGE);
  }
};

export const resolveReferralCode = (): { code?: string; source: ReferralCodeSource } => {
  if (!consumed) {
    const { value: stored, fromMirror } = safeSessionGetItemWithSource(STORAGE_KEY.REF_CODE);
    if (stored) return { code: stored, source: fromMirror ? "memory" : "session" };
    const fromUrl = new URLSearchParams(window.location.search).get(SEARCH_PARAMS.REF_CODE);
    if (fromUrl) return { code: fromUrl, source: "url" };
    if (getRuntimeContext() === "pwa") {
      const fromCookie = getCookie(COOKIE_KEY.REF_CODE);
      if (fromCookie && REF_CODE_FORMAT.test(fromCookie)) return { code: fromCookie, source: "cookie" };
    }
  }
  return { source: "none" };
};

export const getReferralCode = () => resolveReferralCode().code;

export const clearReferralCode = () => {
  consumed = true;
  safeSessionRemoveItem(STORAGE_KEY.REF_CODE);
  deleteCookie(COOKIE_KEY.REF_CODE);
  stripReferralCodeFromUrl();
};

export const getReferralCodeDiagnostics = (): { cookiePresent: boolean; consumed: boolean } => ({
  cookiePresent: !!getCookie(COOKIE_KEY.REF_CODE),
  consumed,
});
